import type { ReactNode } from 'react';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCopyToClipboard } from 'react-use';

import { cn } from '~/lib/cn';

import { Icon } from '../Icon';
import { Tooltip } from '../Tooltip';

interface CopyButtonProps {
  textToCopy: string;
  customIcon?: ReactNode;
  tooltipDuration?: number;
  tooltipContent?: string;
  tooltipContentCopied?: string;
  className?: string;
}

/**
 * CopyButton Component
 *
 * A button component that allows users to copy a given text to the clipboard.
 * It displays a tooltip indicating the copy action was successful for a specified duration.
 *
 * @param textToCopy - The text string to be copied to the clipboard when the button is clicked.
 * @param customIcon - An optional ReactNode to render as the button's custom icon.
 * @param tooltipDuration - The duration (in milliseconds) for which the "Copied" tooltip is displayed. Defaults to 2000ms.
 * @param className - Additional CSS classes to apply to the button.
 *
 * @returns A button with copy functionality and a tooltip indicating success.
 */
function CopyButton({
  textToCopy,
  customIcon,
  tooltipDuration = 1500,
  tooltipContent,
  tooltipContentCopied,
  className,
  ...rest
}: CopyButtonProps) {
  const { t } = useTranslation('components');
  const [state, copyToClipboard] = useCopyToClipboard();
  const [copied, setCopied] = useState(false);
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const timeoutRef = useRef<number | null>(null);

  const handleCopyClick = () => {
    copyToClipboard(textToCopy);

    setCopied(true);
    setTooltipOpen(true);

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = window.setTimeout(() => {
      setCopied(false);
      setTooltipOpen(false);
    }, tooltipDuration);
  };

  const tooltipLabel = state.error
    ? state.error.message
    : copied
      ? tooltipContentCopied || t('copyButton.copied')
      : tooltipContent || t('copyButton.copyText');

  return (
    <div className="relative inline-block">
      <Tooltip
        content={tooltipLabel}
        delayDuration={100}
        sideOffset={4}
        side="top"
        size="sm"
        open={tooltipOpen || copied}
        triggerAsChild
      >
        <button
          type="button"
          aria-label={tooltipContent || t('copyButton.copyText')}
          onClick={handleCopyClick}
          onMouseEnter={() => setTooltipOpen(true)}
          onMouseLeave={() => !copied && setTooltipOpen(false)}
          className={cn(
            'flex size-xl items-center justify-center rounded-full p-xs font-medium transition duration-100 bg-transparent text-icon-strong hover:bg-emphasis-strong active:bg-primary-default disabled:pointer-events-none disabled:bg-transparent disabled:text-icon-disabled',
            className,
          )}
          {...rest}
        >
          {customIcon || <Icon type="copy" size="sm" aria-hidden />}
        </button>
      </Tooltip>
    </div>
  );
}

CopyButton.displayName = 'CopyButton';

export { CopyButton };
